import React, { useEffect, useRef, useState, CSSProperties } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import { Column, ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ClipLoader from "react-spinners/ClipLoader";
import vendorService from "../../services/vendors/vendor.service";
import { UpdateVendorDTO } from "../../models/vendors/vendor-model";
import  {VendorSummaryDTO}  from "../../models/vendors/vendor-model";
import AddVendor from "./AddVendor";


const VendorDataTable: React.FC = () => {
  const [vendors, setVendors] = useState<VendorSummaryDTO[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorSummaryDTO | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
const navigate = useNavigate();

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const data = await vendorService.getAllVendors();
      setVendors(data);
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const showMessage = (summary: string, detail: string, severity: "success" | "error") => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

 const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
  const { newData, index } = e;
  const originalData = vendors[index];

  const updatePayload: UpdateVendorDTO = {
    vendorID: originalData.vendorID,
    firmInfo: newData.firmInfo,
    additionalInfo: newData.additionalInfo || null,
    officeContactNbr: newData.officeContactNbr || null,
    email: newData.email || null,
    address: newData.address || null,
    contactPerson1_Name: newData.contactPerson1_Name || null,
    contactPerson1_Nbr: newData.contactPerson1_Nbr || null,
    contactPerson2_Name: newData.contactPerson2_Name || null,
    contactPerson2_Nbr: newData.contactPerson2_Nbr || null,
    imagePath: newData.imagePath || null,
    isDeleted: originalData.isDeleted, 
    modifiedBy: originalData.createdBy, 
    userID: originalData.createdBy      
  };

  try {
    await vendorService.updateVendor(updatePayload);
    showMessage("Success", "Vendor updated successfully", "success");

    const updatedList = [...vendors];
    updatedList[index] = { ...originalData, ...newData };
    setVendors(updatedList);
  } catch (error) {
    showMessage("Error", "Failed to update vendor", "error");
    console.error("Update error:", error);
  }
};

  const confirmDelete = (vendor: VendorSummaryDTO) => {
    setSelectedVendor(vendor);
    setShowDeleteDialog(true);
  };

  const deleteVendor = async () => {
    if (!selectedVendor) return;

    try {
      await vendorService.deleteVendor(selectedVendor.vendorID);
      showMessage("Success", "Vendor deleted", "success");
      fetchVendors();
    } catch (error) {
      showMessage("Error", "Failed to delete vendor", "error");
      console.error("Delete error:", error);
    } finally {
      setSelectedVendor(null);
      setShowDeleteDialog(false);
    }
  };

const textEditor = (field: keyof VendorSummaryDTO) => (options: ColumnEditorOptions) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    let isValid = true;

    
    if (field === "contactPerson1_Name") {
      isValid = /^[A-Za-z\s]*$/.test(value);
    }

    if (field === "officeContactNbr" || field === "contactPerson1_Nbr") {
      isValid = /^\d*$/.test(value);
    }

    if (isValid) {
      options.editorCallback?.(value);
    }
  };

  return (
    <input
      type="text"
      className="p-inputtext p-component w-full"
      value={options.value || ""}
      onChange={handleChange}
    />
  );
};


  const deleteTemplate = (rowData: VendorSummaryDTO) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      onClick={() => confirmDelete(rowData)}
      tooltip="Delete Vendor"
    />
  );

  const loaderStyle: CSSProperties = {
    display: "block",
    margin: "0 auto",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  return (
    <>
      {loading ? (
        <ClipLoader cssOverride={loaderStyle} size={50} color="#90ee90" loading={loading} />
      ) : (
        <>
          <Toast ref={toast} />
          <div className="card">
           <div className="flex justify-content-between mb-3">
  <h5 className="m-0">Vendors</h5>
  <Button
    label="Add Vendor"
    icon="pi pi-plus"
    className="p-button-success"
    onClick={() => setShowAddDialog(true)}
  />
</div>

        <DataTable
  value={vendors}
  paginator
  rows={10}
  rowsPerPageOptions={[5, 10, 25, 50, 100]}
  stripedRows
  editMode="row"
  onRowEditComplete={onRowEditComplete}
  dataKey="vendorID"
  responsiveLayout="scroll"
  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
  currentPageReportTemplate="{first} to {last} of {totalRecords}"
>

  <Column field="vendorID" header="Vendor ID" style={{ width: "10%" }} />
  <Column field="userName" header="User Name" />
  <Column field="firmInfo" header="Firm Info" editor={textEditor("firmInfo")} />
  <Column field="officeContactNbr" header="Office Contact Number" editor={textEditor("officeContactNbr")} />
  <Column field="address" header="Address" editor={textEditor("address")} />
  <Column field="email" header="Email" editor={textEditor("email")} />
  <Column field="contactPerson1_Name" header="Person Name1" editor={textEditor("contactPerson1_Name")} />
  <Column field="contactPerson1_Nbr" header="Person Number1" editor={textEditor("contactPerson1_Nbr")} />
  {/* <Column field="contactPerson2_Name" header="Person Name2" editor={textEditor("contactPerson2_Name")} />
  <Column field="contactPerson2_Nbr" header="Person Number2" editor={textEditor("contactPerson2_Nbr")} /> */}
  <Column field="additionalInfo" header="Info" editor={textEditor("additionalInfo")} />
  <Column rowEditor header="Edit" style={{ width: "8%" }} />
  <Column body={deleteTemplate} header="Delete" style={{ width: "8%" }} />
</DataTable>

          </div>
        </>
      )}

<Dialog
  visible={showDeleteDialog}
  header="Confirm Delete"
  modal
  onHide={() => setShowDeleteDialog(false)}
  footer={
    <div className="flex justify-content-end gap-2">
      <Button label="No" icon="pi pi-times" onClick={() => setShowDeleteDialog(false)} className="p-button-text" />
      <Button label="Yes" icon="pi pi-check" onClick={deleteVendor} className="p-button-danger" />
    </div>
  }
>
  <p>
    Are you sure you want to delete vendor{" "}
    <strong>{selectedVendor?.firmInfo || selectedVendor?.userName}</strong>?
  </p>
</Dialog>

<Dialog
  visible={showAddDialog}
  header="Add Vendor"
  className="quickview w-50"
  breakpoints={{ "960px": "75vw", "641px": "90vw" }}
  modal
  onHide={() => setShowAddDialog(false)}
>
  <AddVendor
    onSuccess={() => {
      setShowAddDialog(false);
      fetchVendors(); 
    }}
  />
</Dialog>
    </>
  );
};

export default VendorDataTable;
